import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';

const PencilIcon = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M9.5 2.5L11.5 4.5L4.5 11.5H2.5V9.5L9.5 2.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 4L10 6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M2.5 7L5.5 10L11.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const XIcon = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M3 3L11 11M11 3L3 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const TIER_COLORS = {
  1: '#6b7280', 2: '#6b7280',
  3: '#eab308', 4: '#eab308',
  5: '#f97316', 6: '#f97316',
  7: '#ef4444', 8: '#ef4444', 9: '#ef4444', 10: '#ef4444',
};

const formatDiamonds = (n) => {
  if (n == null) return '—';
  if (n === 0) return '0';
  if (n >= 1000000) return `${(n / 1000000)?.toFixed(n % 1000000 === 0 ? 0 : 1)}M`;
  if (n >= 1000) return `${(n / 1000)?.toFixed(0)}K`;
  return String(n);
};

const SkeletonRow = () => (
  <tr>
    {[1,2,3,4,5,6]?.map(i => (
      <td key={i} className="py-3 px-3">
        <div className="h-4 rounded animate-pulse" style={{ backgroundColor: '#1f1f1f', width: i === 1 ? 40 : 60 }} />
      </td>
    ))}
  </tr>
);

const RankupSection = ({ servers, showToast }) => {
  const { t } = useTranslation();
  const [activeServer, setActiveServer] = useState('');
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [showConditionModal, setShowConditionModal] = useState(false);
  const [conditionValues, setConditionValues] = useState({ min_live_days: '', min_live_hours: '' });
  const [savingCondition, setSavingCondition] = useState(false);
  const [advancedBonusMap, setAdvancedBonusMap] = useState({});
  const [togglingBonus, setTogglingBonus] = useState(false);

  useEffect(() => {
    if (servers?.length > 0 && !activeServer) {
      setActiveServer(servers?.[0]?.id);
    }
  }, [servers, activeServer]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [rankupRes, serversRes] = await Promise.all([
      supabase?.from('rankup_config')?.select('*')?.order('tier'),
      supabase?.from('servers')?.select('id, advanced_bonus_active'),
    ]);
    if (rankupRes?.error) {
      showToast(t('admin.config.errorLoadingRankup') + ': ' + rankupRes?.error?.message, 'error');
    } else {
      setAllData(rankupRes?.data || []);
    }
    if (serversRes?.data) {
      const map = {};
      serversRes?.data?.forEach(s => { map[s?.id] = !!s?.advanced_bonus_active; });
      setAdvancedBonusMap(map);
    }
    setLoading(false);
  }, [showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const rows = allData?.filter(r => r?.server === activeServer);
  const color = servers?.find(s => s?.id === activeServer)?.color || '#888888';

  // Condition is same for all rows in a server — take from first row
  const serverCondition = rows?.[0] ? {
    min_live_days: rows?.[0]?.min_live_days,
    min_live_hours: rows?.[0]?.min_live_hours,
  } : null;

  const handleEditClick = (row) => {
    setEditingId(row?.id);
    setEditValues({
      ratio_rankup: String(row?.ratio_rankup ?? ''),
      ratio_maintain: String(row?.ratio_maintain ?? ''),
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };

  const handleSave = async (rowId) => {
    setSaving(true);
    const { error } = await supabase?.from('rankup_config')?.update({
        ratio_rankup: editValues?.ratio_rankup !== '' ? parseFloat(editValues?.ratio_rankup) : null,
        ratio_maintain: parseFloat(editValues?.ratio_maintain) || 0,
      })?.eq('id', rowId);
    setSaving(false);
    if (error) {
      showToast(error?.message, 'error');
    } else {
      showToast(t('admin.config.tierUpdated'), 'success');
      setEditingId(null);
      setEditValues({});
      fetchData();
    }
  };

  const isAdvancedActive = advancedBonusMap?.[activeServer] || false;

  const handleToggleAdvancedBonus = async () => {
    const activate = !isAdvancedActive;
    const delta = activate ? 1 : -1;
    setTogglingBonus(true);
    try {
      // Update all ratio_rankup and ratio_maintain for this server
      const serverRows = allData?.filter(r => r?.server === activeServer);
      const updates = serverRows?.map(row => {
        const newRankup = row?.ratio_rankup != null ? parseFloat(row?.ratio_rankup) + delta : null;
        const newMaintain = row?.ratio_maintain != null && parseFloat(row?.ratio_maintain) > 0 ? parseFloat(row?.ratio_maintain) + delta : parseFloat(row?.ratio_maintain);
        return supabase?.from('rankup_config')?.update({
          ratio_rankup: newRankup,
          ratio_maintain: newMaintain,
        })?.eq('id', row?.id);
      });
      await Promise.all(updates);
      // Update server flag
      await supabase?.from('servers')?.update({ advanced_bonus_active: activate })?.eq('id', activeServer);
      setAdvancedBonusMap(prev => ({ ...prev, [activeServer]: activate }));
      showToast(activate ? t('admin.config.advancedBonusActivated', { server: activeServer, delta: `${activate ? '+' : ''}${delta}` }) : t('admin.config.advancedBonusDeactivated', { server: activeServer, delta: `${delta}` }), 'success');
      fetchData();
    } catch (err) {
      showToast(err?.message, 'error');
    }
    setTogglingBonus(false);
  };

  const handleOpenCondition = () => {
    setConditionValues({
      min_live_days: String(serverCondition?.min_live_days ?? 7),
      min_live_hours: String(serverCondition?.min_live_hours ?? 15),
    });
    setShowConditionModal(true);
  };

  const handleSaveCondition = async () => {
    setSavingCondition(true);
    const { error } = await supabase?.from('rankup_config')?.update({
        min_live_days: parseInt(conditionValues?.min_live_days) || 0,
        min_live_hours: parseFloat(conditionValues?.min_live_hours) || 0,
      })?.eq('server', activeServer);
    setSavingCondition(false);
    if (error) {
      showToast(error?.message, 'error');
    } else {
      showToast(t('admin.config.conditionUpdated', { server: activeServer }), 'success');
      setShowConditionModal(false);
      fetchData();
    }
  };

  return (
    <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
      {/* Header */}
      <div className="px-6 py-4 border-b" style={{ borderColor: '#1f1f1f' }}>
        <h2 className="text-white font-semibold text-base" style={{ fontFamily: 'Sora, sans-serif' }}>
          {t('admin.config.rankupTitle')}
        </h2>
        <p className="text-white/40 text-xs mt-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>
          {t('admin.config.rankupSubtitle')}
        </p>
      </div>
      {/* Server Tabs + Condition Button */}
      <div className="flex items-center justify-between px-6 pt-4 pb-2 flex-wrap gap-2">
        <div className="flex gap-1 flex-wrap">
          {servers?.map(server => {
            const isActive = activeServer === server?.id;
            const c = server?.color || '#888888';
            return (
              <button
                key={server?.id}
                onClick={() => setActiveServer(server?.id)}
                className="px-4 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150"
                style={{
                  fontFamily: 'Inter, sans-serif',
                  backgroundColor: isActive ? `${c}22` : 'transparent',
                  color: isActive ? c : 'rgba(255,255,255,0.35)',
                  border: `1px solid ${isActive ? c : '#1f1f1f'}`,
                }}
              >
                {server?.id}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {serverCondition && (
            <button
              onClick={handleOpenCondition}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
              style={{ backgroundColor: `${color}18`, color, border: `1px solid ${color}30`, fontFamily: 'Inter, sans-serif' }}
            >
              <PencilIcon />
              {t('admin.config.editCondition')} ({serverCondition?.min_live_days}{t('admin.dashboard.daysShort', 'd')} / {serverCondition?.min_live_hours}h)
            </button>
          )}
          <button
            onClick={handleToggleAdvancedBonus}
            disabled={togglingBonus || loading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
            style={{
              backgroundColor: isAdvancedActive ? '#22c55e18' : '#a855f718',
              color: isAdvancedActive ? '#22c55e' : '#a855f7',
              border: `1px solid ${isAdvancedActive ? '#22c55e30' : '#a855f730'}`,
              fontFamily: 'Inter, sans-serif',
              opacity: togglingBonus ? 0.6 : 1,
            }}
          >
            {isAdvancedActive ? t('admin.config.advancedBonusActiveLabel') : t('admin.config.activateAdvancedBonus')}
          </button>
        </div>
      </div>
      {/* Table */}
      <div className="px-6 pb-6 pt-2 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
              {[t('admin.config.colTier'), t('admin.config.colMinDiamonds'), t('admin.config.colRankingUp'), t('admin.config.colMaintaining'), t('admin.config.colCondition'), '']?.map(col => (
                <th
                  key={col}
                  className="text-left py-2.5 px-3 text-xs font-medium"
                  style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [1,2,3,4,5,6,7,8,9,10]?.map(i => <SkeletonRow key={i} />)
            ) : rows?.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-8 text-center text-white/30 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {t('admin.config.noDataServer')}
                </td>
              </tr>
            ) : (
              rows?.map((row, i) => {
                const isEditing = editingId === row?.id;
                const tierColor = TIER_COLORS?.[row?.tier] || '#6b7280';
                const ratioRankup = row?.ratio_rankup != null ? parseFloat(row?.ratio_rankup)?.toFixed(1) + '%' : '—';
                const ratioMaintain = row?.ratio_maintain != null ? parseFloat(row?.ratio_maintain)?.toFixed(1) + '%' : '0%';

                return (
                  <tr
                    key={row?.id}
                    style={{ borderBottom: i < rows?.length - 1 ? '1px solid #1a1a1a' : 'none' }}
                  >
                    {/* Tier */}
                    <td className="py-3 px-3">
                      <span
                        className="inline-flex items-center justify-center w-9 h-6 rounded text-xs font-bold"
                        style={{ backgroundColor: `${tierColor}22`, color: tierColor, fontFamily: 'Sora, sans-serif' }}
                      >
                        T{row?.tier}
                      </span>
                    </td>
                    {/* Min Diamonds */}
                    <td className="py-3 px-3 text-sm text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                      💎 {formatDiamonds(row?.min_diamonds)}
                    </td>
                    {/* Ranking Up % */}
                    <td className="py-3 px-3">
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.001"
                          value={editValues?.ratio_rankup}
                          onChange={e => setEditValues(v => ({ ...v, ratio_rankup: e?.target?.value }))}
                          placeholder="0.06"
                          className="rounded px-2 py-1 text-base text-white outline-none border w-20"
                          style={{ backgroundColor: '#1a1a1a', borderColor: color, fontFamily: 'Inter, sans-serif' }}
                        />
                      ) : (
                        row?.ratio_rankup == null ? (
                          <span className="text-sm text-white/30" style={{ fontFamily: 'Inter, sans-serif' }}>—</span>
                        ) : (
                          <span className="text-sm font-semibold" style={{ color, fontFamily: 'Inter, sans-serif' }}>
                            {ratioRankup}
                          </span>
                        )
                      )}
                    </td>
                    {/* Maintaining % */}
                    <td className="py-3 px-3">
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.001"
                          value={editValues?.ratio_maintain}
                          onChange={e => setEditValues(v => ({ ...v, ratio_maintain: e?.target?.value }))}
                          placeholder="0.04"
                          className="rounded px-2 py-1 text-base text-white outline-none border w-20"
                          style={{ backgroundColor: '#1a1a1a', borderColor: color, fontFamily: 'Inter, sans-serif' }}
                        />
                      ) : (
                        <span className="text-sm text-white/60" style={{ fontFamily: 'Inter, sans-serif' }}>
                          {ratioMaintain}
                        </span>
                      )}
                    </td>
                    {/* Condition */}
                    <td className="py-3 px-3">
                      <span className="text-xs text-white/40" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {row?.min_live_days}{t('admin.dashboard.daysShort', 'd')} / {row?.min_live_hours}h
                      </span>
                    </td>
                    {/* Actions */}
                    <td className="py-3 px-3">
                      {isEditing ? (
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => handleSave(row?.id)}
                            disabled={saving}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all"
                            style={{ backgroundColor: '#22c55e22', color: '#22c55e', border: '1px solid #22c55e40', opacity: saving ? 0.6 : 1 }}
                          >
                            <CheckIcon /> {t('admin.config.saveShort')}
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs"
                            style={{ backgroundColor: '#1f1f1f', color: 'rgba(255,255,255,0.4)' }}
                          >
                            <XIcon />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEditClick(row)}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
                          style={{ backgroundColor: `${color}18`, color, border: `1px solid ${color}30` }}
                        >
                          <PencilIcon />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {/* Condition Modal */}
      {showConditionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
          <div className="rounded-2xl border p-6 w-full max-w-sm mx-4" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
            <h3 className="text-white font-bold text-base mb-2" style={{ fontFamily: 'Sora, sans-serif' }}>
              {t('admin.config.editConditionTitle', { server: activeServer })}
            </h3>
            <p className="text-white/40 text-xs mb-5" style={{ fontFamily: 'Inter, sans-serif' }}>
              {t('admin.config.appliedToAllTiers')}
            </p>
            <div className="flex flex-col gap-4">
              <div>
                <label className="text-white/50 text-xs mb-1.5 block" style={{ fontFamily: 'Inter, sans-serif' }}>Min Live Days</label>
                <input
                  type="number"
                  value={conditionValues?.min_live_days}
                  onChange={e => setConditionValues(v => ({ ...v, min_live_days: e?.target?.value }))}
                  className="w-full rounded-lg px-3 py-2 text-base text-white outline-none border"
                  style={{ backgroundColor: '#0d0d0d', borderColor: '#2a2a2a', fontFamily: 'Inter, sans-serif' }}
                />
              </div>
              <div>
                <label className="text-white/50 text-xs mb-1.5 block" style={{ fontFamily: 'Inter, sans-serif' }}>Min Live Hours</label>
                <input
                  type="number"
                  step="0.5"
                  value={conditionValues?.min_live_hours}
                  onChange={e => setConditionValues(v => ({ ...v, min_live_hours: e?.target?.value }))}
                  className="w-full rounded-lg px-3 py-2 text-base text-white outline-none border"
                  style={{ backgroundColor: '#0d0d0d', borderColor: '#2a2a2a', fontFamily: 'Inter, sans-serif' }}
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSaveCondition}
                disabled={savingCondition}
                className="flex-1 py-2.5 rounded-lg text-sm font-bold transition-all"
                style={{ backgroundColor: '#22c55e22', color: '#22c55e', border: '1px solid #22c55e40', fontFamily: 'Sora, sans-serif', opacity: savingCondition ? 0.6 : 1 }}
              >
                {savingCondition ? t('admin.config.savingLabel') : t('admin.config.saveLabel')}
              </button>
              <button
                onClick={() => setShowConditionModal(false)}
                className="px-4 py-2.5 rounded-lg text-sm font-semibold"
                style={{ backgroundColor: '#1f1f1f', color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}
              >
                {t('admin.config.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RankupSection;
